// «Холодильник»: купили — значит есть, пока не отметили «Закончилось». Что приготовить из этого
// и меню на неделю, которое одной кнопкой превращается в список покупок.
import type { DishIdeaDto, ListsState, MealPlanDto, PantryItemDto, ShoppingLineDto } from "@home/shared";
import { useEffect, useRef, useState } from "react";
import { api } from "../api.ts";
import { dayTitle, todayIso } from "../format.ts";
import { t, unitLabel, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { engine, useSync, visibleLists } from "../store.ts";
import { newId } from "../sync/engine.ts";
import { hapticSelect, hapticTap } from "../telegram.ts";
import { sameNameKey } from "../quickAdd.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { EmptyState } from "./EmptyState.tsx";
import { ProductSheet } from "./Prices.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

const normalize = sameNameKey;

/**
 * Добавить в список покупок. listName — в список с этим названием (меню на неделю: повторное нажатие дописывает
 * в тот же список, а не создаёт второй); иначе — в последний список продуктов семьи. Списка нет — создаём.
 * Что уже есть в списке и не куплено — не повторяем. Через движок синхронизации: работает и без сети.
 */
export async function addToShoppingList(state: ListsState, lines: ShoppingLineDto[], listName?: string): Promise<{ id: string; name: string }> {
  const lists = visibleLists(state, false);
  const existing = listName ? lists.find((l) => l.name === listName) : lists.find((l) => l.kind === "grocery");
  const list = existing ?? { id: newId(), name: listName ?? t("groceryListName") };
  if (!existing) {
    await engine.mutate({ type: "list.create", id: list.id, name: list.name, kind: "grocery", visibility: "family", isTemplate: false });
  }
  const have = new Set(
    Object.values(state.items)
      .filter((i) => i.listId === list.id && !i.deletedAt && !i.isChecked)
      .map((i) => normalize(i.name)),
  );
  for (const line of lines) {
    if (have.has(normalize(line.name))) continue;
    have.add(normalize(line.name));
    await engine.mutate({ type: "item.create", id: newId(), listId: list.id, name: line.name, qty: line.qty, unit: line.unit });
  }
  return { id: list.id, name: list.name };
}

function boughtText(item: PantryItemDto): string {
  if (item.daysAgo === 0) return t("boughtToday");
  if (item.daysAgo === 1) return t("boughtYesterday");
  return t("boughtDaysAgo", { n: item.daysAgo });
}

function qtyText(item: PantryItemDto): string | null {
  if (item.qty === null) return null;
  return `${String(item.qty).replace(".", ",")}${item.unit ? ` ${unitLabel(item.unit)}` : ""}`;
}

/** Метка у названия: 🥦 полезное, 🍭 вредное; обычное — без значка. */
function healthMark(item: PantryItemDto): string {
  return item.health === "healthy" ? " 🥦" : item.health === "unhealthy" ? " 🍭" : "";
}

/** «Ещё → Дом → Холодильник». */
export function FridgeSheet({ lang, onClose, onOpenList }: { lang: Lang; onClose: () => void; onOpenList: (id: string) => void }) {
  const { state } = useSync();
  const remote = useRemote("pantry", () => api.pantry());
  const [sheet, setSheet] = useState<"add" | "ideas" | "menu" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [product, setProduct] = useState<PantryItemDto | null>(null);
  const d = remote.data;
  // Старый кэш (до «скоро закончится») — без раздела soon.
  const soon = d?.soon ?? [];
  const empty = d && soon.length + d.fresh.length + d.stale.length + d.used.length === 0;

  const mark = async (item: PantryItemDto, used: boolean) => {
    if (busy) return;
    hapticTap();
    setBusy(item.id);
    try {
      await api.pantryUsed(item.id, used);
      toast(used ? t("markedUsed") : t("restored"));
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const toList = async (item: PantryItemDto) => {
    hapticTap();
    const list = await addToShoppingList(state, [{ name: item.name, qty: null, unit: null }]);
    toast(t("addedToList", { name: list.name }));
  };

  const open = (item: PantryItemDto) => {
    // Данные из кэша до обновления приложения — без товара: карточку не открываем.
    if (!item.productId) return;
    hapticSelect();
    setProduct(item);
  };

  /** Строка продукта: нажатие — карточка товара (метка, как часто покупаете, цены). */
  const rows = (items: PantryItemDto[], due = false) => (
    <ul className="row-list">
      {items.map((item) => (
        <li key={item.id} className="row">
          <button type="button" className="row-main stack" onClick={() => open(item)}>
            <span className="row-title">
              {item.name}
              {healthMark(item)}
            </span>
            <span className="row-meta">
              {[
                due && item.dueIn !== null ? (item.dueIn <= 0 ? t("dueNow") : t("dueSoon")) : null,
                boughtText(item),
                qtyText(item),
                item.everyDays ? t("lastsDays", { n: item.everyDays }) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </button>
          {/* Скоро закончится — сразу в список (🛒); закончилось — как у всех. */}
          {due && (
            <button type="button" className="btn btn-small-plain icon-only" aria-label={t("toListBtn")} title={t("toListBtn")} onClick={() => void toList(item)}>
              🛒
            </button>
          )}
          <button type="button" className="btn btn-small-plain" disabled={busy === item.id} onClick={() => void mark(item, true)}>
            {t("usedBtn")}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <Sheet title={`🧊 ${t("fridgeTitle")}`} onClose={onClose}>
      <div className="fridge-actions">
        <button type="button" className="btn" onClick={() => setSheet("ideas")} disabled={!d || soon.length + d.fresh.length + d.stale.length === 0}>
          💡 {t("cookIdeas")}
        </button>
        <button type="button" className="btn" onClick={() => setSheet("menu")}>
          🍽 {t("menuTitle")}
        </button>
      </div>
      {!d ? (
        <div className="skeleton skeleton-block" />
      ) : empty ? (
        <EmptyState icon="🧊" title={t("fridgeEmpty")} text={t("fridgeEmptyHint")} />
      ) : (
        <>
          {soon.length > 0 && (
            <>
              <div className="section-title">⏳ {t("fridgeSoon")}</div>
              {rows(soon, true)}
            </>
          )}
          {d.fresh.length > 0 && (
            <>
              <div className="section-title">{t("fridgeHave")}</div>
              {rows(d.fresh)}
            </>
          )}
          {d.stale.length > 0 && (
            <>
              <div className="section-title">{t("fridgeStale")}</div>
              {rows(d.stale)}
            </>
          )}
          {d.used.length > 0 && (
            <>
              <div className="section-title">{t("fridgeUsed")}</div>
              <p className="hint">{t("fridgeUsedHint")}</p>
              <ul className="row-list">
                {d.used.map((item) => (
                  <li key={item.id} className="row">
                    <button type="button" className="row-main" disabled={busy === item.id} onClick={() => void mark(item, false)}>
                      <span className="row-title faded-text">{item.name}</span>
                    </button>
                    <button type="button" className="btn btn-small-plain" onClick={() => void toList(item)}>
                      🛒 {t("toListBtn")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <PrimaryAction label={`＋ ${t("addProduct")}`} onClick={() => setSheet("add")} />
      {sheet === "add" && <AddPantrySheet onClose={() => setSheet(null)} />}
      {sheet === "ideas" && <IdeasSheet onClose={() => setSheet(null)} />}
      {sheet === "menu" && <MenuSheet lang={lang} onClose={() => setSheet(null)} onOpenList={onOpenList} />}
      {product && <ProductSheet id={product.productId} name={product.name} lang={lang} onClose={() => setProduct(null)} />}
    </Sheet>
  );
}

function AddPantrySheet({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (busy) return;
    if (!name.trim()) return setError(t("fillName"));
    setBusy(true);
    setError(null);
    try {
      await api.pantryAdd(name.trim());
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Sheet title={`＋ ${t("addProduct")}`} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span>{t("productName")}</span>
          <input className="input" placeholder={t("productNamePh")} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={t("save")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

/** Идеи блюд из того, что есть; недостающее — в список покупок. */
function IdeasSheet({ onClose }: { onClose: () => void }) {
  const { state } = useSync();
  const [dishes, setDishes] = useState<DishIdeaDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      setDishes((await api.cookingIdeas()).dishes);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  // Один запрос при открытии (StrictMode в разработке монтирует дважды — а каждый вызов Claude платный).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, []);

  const buy = async (dish: DishIdeaDto) => {
    hapticTap();
    const list = await addToShoppingList(
      state,
      dish.missing.map((name) => ({ name, qty: null, unit: null })),
    );
    toast(t("addedToList", { name: list.name }));
  };

  return (
    <Sheet title={`💡 ${t("ideasTitle")}`} onClose={onClose}>
      {busy && !dishes ? (
        <>
          <p className="hint">{t("ideasLoading")}</p>
          <div className="skeleton skeleton-block" />
        </>
      ) : error && !dishes ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : (
        <ul className="dish-list">
          {(dishes ?? []).map((dish) => (
            <li key={dish.name} className="dish">
              <div className="dish-head">
                <b>{dish.name}</b>
                <span className="muted">{t("minutesN", { n: dish.minutes })}</span>
              </div>
              {dish.uses.length > 0 && <div className="dish-line">✅ {t("ideaHave", { list: dish.uses.join(", ") })}</div>}
              {dish.missing.length > 0 && <div className="dish-line">🛒 {t("ideaMissing", { list: dish.missing.join(", ") })}</div>}
              <p className="dish-how">{dish.how}</p>
              {dish.missing.length > 0 && (
                <button type="button" className="btn btn-small-plain" onClick={() => void buy(dish)}>
                  🛒 {t("toListBtn")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && dishes && <p className="form-error">{error}</p>}
      <PrimaryAction label={dishes ? t("ideasMore") : t("cookIdeas")} onClick={() => void load()} busy={busy} />
    </Sheet>
  );
}

function weekday(date: string): string {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay() || 7;
  return t("weekdaysFull").split(",")[d - 1] ?? "";
}

/** Меню на неделю: обед и ужин по дням, что докупить → новый список покупок. */
export function MenuSheet({ lang, onClose, onOpenList }: { lang: Lang; onClose: () => void; onOpenList: (id: string) => void }) {
  const { state } = useSync();
  const remote = useRemote("meal-plan", () => api.mealPlan());
  const [plan, setPlan] = useState<MealPlanDto | null>(null);
  const [remake, setRemake] = useState(false);
  const [wishes, setWishes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = plan ?? remote.data?.plan ?? null;
  const today = todayIso();

  const make = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.makeMealPlan(wishes.trim() || null);
      hapticTap();
      setPlan(res.plan);
      setRemake(false);
      invalidateRemote();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const toList = async (p: MealPlanDto) => {
    hapticTap();
    // Свой список на каждое меню: «Меню на неделю 04.10–10.10».
    const span = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
    const name = `${t("menuListName")} ${span(p.days[0]!.date)}–${span(p.days.at(-1)!.date)}`;
    const list = await addToShoppingList(state, p.shopping, name);
    toast(t("addedToList", { name: list.name }));
    onOpenList(list.id);
  };

  const form = (
    <div className="form">
      {!current && <EmptyState icon="🍽" title={t("menuTitle")} text={t("menuEmptyHint")} />}
      <label className="field">
        <span>{t("wishesL")}</span>
        <input className="input" placeholder={t("wishesPh")} value={wishes} maxLength={200} onChange={(e) => setWishes(e.target.value)} />
      </label>
      {busy && <p className="hint">{t("menuLoading")}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <PrimaryAction label={`✨ ${t("makeMenu")}`} onClick={() => void make()} busy={busy} />
    </div>
  );

  return (
    <Sheet title={`🍽 ${t("menuTitle")}`} onClose={onClose}>
      {!remote.data && remote.loading && !plan ? (
        <div className="skeleton skeleton-block" />
      ) : !current || remake ? (
        form
      ) : (
        <>
          <ul className="row-list">
            {current.days.map((day) => (
              <li key={day.date} className={day.date === today ? "row menu-day today" : "row menu-day"}>
                <div className="row-body">
                  <div className="row-title">
                    {dayTitle(day.date, lang)} · {weekday(day.date)}
                  </div>
                  <div className="row-meta">
                    {t("lunchL")}: {day.lunch}
                  </div>
                  <div className="row-meta">
                    {t("dinnerL")}: {day.dinner}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {current.shopping.length > 0 && (
            <>
              <div className="section-title">{t("shoppingN", { n: current.shopping.length })}</div>
              <ul className="plain-list">
                {current.shopping.map((line) => (
                  <li key={line.name}>
                    <span>{line.name}</span>
                    {line.qty !== null && (
                      <b>
                        {String(line.qty).replace(".", ",")} {line.unit ? unitLabel(line.unit) : ""}
                      </b>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          <button
            type="button"
            className="btn btn-block"
            onClick={() => {
              hapticSelect();
              setRemake(true);
            }}
          >
            {t("remakeMenu")}
          </button>
          {current.shopping.length > 0 && <PrimaryAction label={`🛒 ${t("toShoppingList")}`} onClick={() => void toList(current)} />}
        </>
      )}
    </Sheet>
  );
}
